import { useEffect, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, type PieLabelRenderProps } from "recharts";
import type { ChartDatum } from "@/lib/analytics";
import { BRAND } from "@/lib/brand-colors";

const PIE_ANIMATION = { animationDuration: 900, animationEasing: "ease-out" as const };

const GLASS_DONUT_COLORS = [
  BRAND.accent,
  "#7CB9FF",
  "#FF8A4C",
  "#F472B6",
  "#B2B2BE",
  "#A78BFA",
  "#86EFAC",
  "#FCD34D",
] as const;

function prepareDonutData(data: ChartDatum[], maxSlices = 5): ChartDatum[] {
  if (data.length <= maxSlices) return data;

  const top = data.slice(0, maxSlices);
  const otherValue = data.slice(maxSlices).reduce((sum, entry) => sum + entry.value, 0);
  if (otherValue <= 0) return top;

  return [...top, { name: "Other", value: otherValue }];
}

function shortLabel(name: string, maxLength = 11): string {
  if (name.length <= maxLength) return name;
  return `${name.slice(0, maxLength - 1)}…`;
}

function GlassSegmentLabel({ frosted, ...props }: PieLabelRenderProps & { frosted: boolean }) {
  const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, percent = 0, name = "" } = props;
  if (!percent || percent < 0.04 || !frosted) return null;

  const radian = Math.PI / 180;
  const labelRadius = Number(outerRadius) + 22;
  const x = Number(cx) + labelRadius * Math.cos(-midAngle * radian);
  const y = Number(cy) + labelRadius * Math.sin(-midAngle * radian);
  const pct = Math.round(percent * 100);
  const label = `${pct}% ${shortLabel(String(name))}`;

  return (
    <foreignObject x={x - 42} y={y - 10} width={84} height={20} className="overflow-visible">
      <div
        className="flex h-5 w-full items-center justify-center rounded-full border border-white/80 bg-white/35 shadow-[0_2px_12px_rgba(255,255,255,0.25)] backdrop-blur-md"
        style={{
          WebkitBackdropFilter: "blur(10px)",
          backdropFilter: "blur(10px)",
        }}
      >
        <span
          className="truncate px-2 text-[10px] font-semibold leading-none"
          style={{ color: BRAND.foreground }}
        >
          {label}
        </span>
      </div>
    </foreignObject>
  );
}

function DonutTooltip({
  active,
  payload,
  valueLabel,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ name?: string | number; value?: unknown }>;
  valueLabel: string;
}) {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  const value = entry?.value;
  if (value == null) return null;

  return (
    <div className="animate-scale rounded-2xl border border-white/60 bg-surface/95 px-3.5 py-2.5 shadow-[var(--shadow-pop)] backdrop-blur-md">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
        {String(entry?.name ?? "")}
      </p>
      <p className="mt-0.5 text-sm font-extrabold tracking-tight text-foreground">
        {String(value)} <span className="font-semibold text-muted">{valueLabel}</span>
      </p>
    </div>
  );
}

export function GlassDonutChart({
  data,
  valueLabel = "services",
  maxSlices = 5,
  minLabelPercent = 0.04,
}: {
  data: ChartDatum[];
  valueLabel?: string;
  maxSlices?: number;
  minLabelPercent?: number;
}) {
  const chartData = prepareDonutData(data, maxSlices);
  const chartKey = chartData.map((entry) => `${entry.name}:${entry.value}`).join("|");
  const [labelsFrosted, setLabelsFrosted] = useState(false);

  useEffect(() => {
    setLabelsFrosted(false);
  }, [chartKey]);

  useEffect(() => {
    if (chartData.length === 0) {
      setLabelsFrosted(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setLabelsFrosted(true);
    }, PIE_ANIMATION.animationDuration + 50);

    return () => window.clearTimeout(timer);
  }, [chartKey, chartData.length]);

  function renderLabel(props: PieLabelRenderProps) {
    if (!props.percent || props.percent < minLabelPercent) return null;
    return <GlassSegmentLabel {...props} frosted={labelsFrosted} />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 16, right: 16, left: 16, bottom: 16 }}>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius="38%"
          outerRadius="80%"
          paddingAngle={4}
          cornerRadius={12}
          stroke="rgba(255,255,255,0.65)"
          strokeWidth={2}
          label={renderLabel}
          labelLine={false}
          onAnimationEnd={() => setLabelsFrosted(true)}
          {...PIE_ANIMATION}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={entry.name}
              fill={index === 0 ? GLASS_DONUT_COLORS[0] : GLASS_DONUT_COLORS[(index + 1) % GLASS_DONUT_COLORS.length]}
              fillOpacity={0.92}
            />
          ))}
        </Pie>
        <Tooltip
          content={({ active, payload }) => (
            <DonutTooltip
              active={active}
              payload={payload as ReadonlyArray<{ name?: string | number; value?: unknown }> | undefined}
              valueLabel={valueLabel}
            />
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
